import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from '../utils/translations';
import axios from 'axios';
import { Wind, Waves, Zap, ArrowRight, Shield, Award, MapPin } from 'lucide-react';
import InfoAlerts from '../components/InfoAlerts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const [latestNews, setLatestNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLatestNews = async () => {
      try {
        const response = await axios.get(`${API}/news?published=true`);
        setLatestNews(response.data.slice(0, 3));
      } catch (error) {
        console.error('Error fetching news:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchLatestNews();
  }, []);

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section with Background Image */}
      <section className="relative h-[90vh] overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img 
            src="/hero-soma-bay.jpg" 
            alt="Surfmotion Soma Bay - Windsurfing and Kitesurfing Paradise"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/60 via-blue-900/40 to-cyan-900/50"></div>
        </div>
        
        {/* Hero Content */}
        <div className="relative h-full flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className="text-center text-white">
              <h1 className="text-6xl md:text-7xl font-bold mb-6 animate-fade-in" data-testid="hero-title">
                {t('home.hero_title')}
              </h1>
              <p className="text-2xl md:text-3xl mb-12 text-gray-100 font-light" data-testid="hero-subtitle">
                {t('home.hero_subtitle')}
              </p>
              <a
                href="https://reservations.surfmotion.com/reservation"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-button"
                className="inline-flex items-center bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-10 py-4 rounded-full font-semibold text-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 transform"
              >
                {t('home.hero_cta')}
                <ArrowRight className="ml-2 w-6 h-6" />
              </a>
            </div>
          </div>
        </div>
        
        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white rounded-full mt-2"></div>
          </div>
        </div>
      </section>

      {/* Info Alerts Section - Prominent placement after hero */}
      <InfoAlerts />

      {/* Activities Section */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-4" data-testid="activities-title">
            {t('home.activities')}
          </h2>
          <p className="text-center text-gray-600 mb-16 text-lg">
            {language === 'de' ? 'Entdecke deine Leidenschaft am Wasser' : 'Discover your passion on the water'}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Windsurfing */}
            <Link
              to="/windsurfing"
              data-testid="activity-card-windsurfing"
              className="group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
            >
              <div className="relative h-80">
                <img 
                  src="https://images.unsplash.com/photo-1619507845851-1ada1136b795?crop=entropy&cs=srgb&fm=jpg&q=85" 
                  alt="Windsurfing"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/90 via-blue-900/50 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <Wind className="w-12 h-12 mb-3 text-cyan-400" />
                  <h3 className="text-3xl font-bold mb-2">
                    {t('nav.windsurfing')}
                  </h3>
                  <p className="text-blue-100">
                    {language === 'de'
                      ? 'Perfekte Bedingungen für Anfänger und Profis.'
                      : 'Perfect conditions for beginners and pros.'}
                  </p>
                </div>
              </div>
            </Link>

            {/* Kitesurfing */}
            <Link
              to="/kitesurfing"
              data-testid="activity-card-kitesurfing"
              className="group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
            >
              <div className="relative h-80">
                <img 
                  src="https://images.unsplash.com/photo-1627068477565-3a66d5f76d5e?crop=entropy&cs=srgb&fm=jpg&q=85" 
                  alt="Kitesurfing"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-purple-900/90 via-purple-900/50 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <Waves className="w-12 h-12 mb-3 text-pink-400" />
                  <h3 className="text-3xl font-bold mb-2">
                    {t('nav.kitesurfing')}
                  </h3>
                  <p className="text-purple-100">
                    {language === 'de'
                      ? 'Konstante Winde und sichere Bereiche.'
                      : 'Constant winds and safe areas.'}
                  </p>
                </div>
              </div>
            </Link>

            {/* Wingfoiling */}
            <Link
              to="/wingfoiling"
              data-testid="activity-card-wingfoiling"
              className="group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
            >
              <div className="relative h-80">
                <img 
                  src="https://images.unsplash.com/photo-1615840956750-3262c5fffb62?crop=entropy&cs=srgb&fm=jpg&q=85" 
                  alt="Wingfoiling"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-orange-900/90 via-orange-900/50 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <Zap className="w-12 h-12 mb-3 text-yellow-400" />
                  <h3 className="text-3xl font-bold mb-2">
                    {t('nav.wingfoiling')}
                  </h3>
                  <p className="text-orange-100">
                    {language === 'de'
                      ? 'Der neueste Trendsport am Wasser.'
                      : 'The latest trend in watersports.'}
                  </p>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Us Section */}
      <section className="py-20 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-purple-50 to-cyan-50"></div>
        <div className="absolute inset-0 opacity-10" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%239C92AC" fill-opacity="0.4"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'}}>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-4">{t('home.why_us')}</h2>
          <p className="text-center text-gray-600 mb-16 text-lg">
            {language === 'de' ? 'Was uns besonders macht' : 'What makes us special'}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="group bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl mb-6 group-hover:scale-110 transition-transform">
                <Award className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-3">{t('home.quality')}</h3>
              <p className="text-gray-600 text-lg">{t('home.quality_desc')}</p>
            </div>
            <div className="group bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl mb-6 group-hover:scale-110 transition-transform">
                <Shield className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-3">{t('home.safety')}</h3>
              <p className="text-gray-600 text-lg">{t('home.safety_desc')}</p>
            </div>
            <div className="group bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl mb-6 group-hover:scale-110 transition-transform">
                <MapPin className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-3">{t('home.location')}</h3>
              <p className="text-gray-600 text-lg">{t('home.location_desc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Latest News Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-12">
            <div>
              <h2 className="text-4xl font-bold mb-2" data-testid="latest-news-title">{t('home.latest_news')}</h2>
              <p className="text-gray-600">{language === 'de' ? 'Die neuesten Updates aus Soma Bay' : 'Latest updates from Soma Bay'}</p>
            </div>
            <Link
              to="/news"
              data-testid="view-all-news-link"
              className="hidden md:flex items-center text-blue-600 hover:text-blue-700 font-semibold px-6 py-3 rounded-full bg-blue-50 hover:bg-blue-100 transition-all"
            >
              {t('home.view_all')}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </div>

          {loading ? (
            <div className="text-center py-12" data-testid="news-loading">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : latestNews.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-testid="news-grid">
              {latestNews.map((news) => (
                <div key={news.id} className="group bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                  {news.image_url ? (
                    <div className="relative h-56 overflow-hidden">
                      <img 
                        src={news.image_url} 
                        alt={language === 'de' ? news.title_de : news.title_en} 
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute top-4 left-4">
                        <span className="px-3 py-1 text-xs font-semibold rounded-full bg-white/90 text-blue-600 backdrop-blur-sm">
                          {news.category}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="h-56 bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
                      <div className="text-white text-xs font-semibold px-3 py-1 rounded-full bg-white/20 backdrop-blur-sm">
                        {news.category}
                      </div>
                    </div>
                  )}
                  <div className="p-6">
                    <h3 className="text-xl font-bold mb-3 group-hover:text-blue-600 transition-colors">
                      {language === 'de' ? news.title_de : news.title_en}
                    </h3>
                    <p className="text-gray-600 mb-4 line-clamp-2">
                      {language === 'de' ? news.excerpt_de || news.content_de : news.excerpt_en || news.content_en}
                    </p>
                    <Link 
                      to={`/news/${news.id}`} 
                      className="inline-flex items-center text-blue-600 hover:text-blue-700 font-semibold group"
                    >
                      {t('newsPage.read_more')}
                      <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-2xl">
              <p className="text-gray-500">{language === 'de' ? 'Keine News verfügbar' : 'No news available'}</p>
            </div>
          )}
          
          <div className="mt-8 text-center md:hidden">
            <Link
              to="/news"
              className="inline-flex items-center text-blue-600 hover:text-blue-700 font-semibold px-6 py-3 rounded-full bg-blue-50 hover:bg-blue-100 transition-all"
            >
              {t('home.view_all')}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Watersports Info Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">{t('home.sports_title')}</h2>
          </div>

          <div className="space-y-12">
            {/* Windsurfing Info */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div className="order-2 lg:order-1">
                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-8">
                  <Wind className="w-12 h-12 text-blue-600 mb-4" />
                  <h3 className="text-3xl font-bold mb-4">Windsurfing</h3>
                  <p className="text-gray-700 leading-relaxed">{t('home.windsurfing_info')}</p>
                </div>
              </div>
              <div className="order-1 lg:order-2 rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src="https://images.unsplash.com/photo-1619507845851-1ada1136b795?crop=entropy&cs=srgb&fm=jpg&q=85" 
                  alt="Windsurfing"
                  className="w-full h-80 object-cover"
                />
              </div>
            </div>

            {/* Kitesurfing Info */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src="https://images.unsplash.com/photo-1627068477565-3a66d5f76d5e?crop=entropy&cs=srgb&fm=jpg&q=85" 
                  alt="Kitesurfing"
                  className="w-full h-80 object-cover"
                />
              </div>
              <div>
                <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8">
                  <Waves className="w-12 h-12 text-purple-600 mb-4" />
                  <h3 className="text-3xl font-bold mb-4">Kitesurfing</h3>
                  <p className="text-gray-700 leading-relaxed">{t('home.kitesurfing_info')}</p>
                </div>
              </div>
            </div>

            {/* Wingfoiling Info */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div className="order-2 lg:order-1">
                <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-2xl p-8">
                  <Zap className="w-12 h-12 text-orange-600 mb-4" />
                  <h3 className="text-3xl font-bold mb-4">Wingfoiling</h3>
                  <p className="text-gray-700 leading-relaxed">{t('home.wingfoiling_info')}</p>
                </div>
              </div>
              <div className="order-1 lg:order-2 rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src="https://images.unsplash.com/photo-1666175497932-8c88bbad53b1?crop=entropy&cs=srgb&fm=jpg&q=85" 
                  alt="Wingfoiling"
                  className="w-full h-80 object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Region Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">{t('home.region_title')}</h2>
            <p className="text-xl text-gray-600">{t('home.region_subtitle')}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">{t('home.region_desc')}</p>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1544551763-46a013bb70d5?crop=entropy&cs=srgb&fm=jpg&q=85" 
                alt="Soma Bay beach"
                className="w-full h-80 object-cover"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h3 className="text-2xl font-bold mb-4">{t('home.hurghada_title')}</h3>
              <p className="text-gray-700 leading-relaxed">{t('home.hurghada_desc')}</p>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h3 className="text-2xl font-bold mb-4">{t('home.activities_title')}</h3>
              <p className="text-gray-700 leading-relaxed">{t('home.activities_desc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Climate Section */}
      <section className="py-20 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">{t('home.climate_title')}</h2>
          <p className="text-lg text-gray-700 leading-relaxed">{t('home.climate_desc')}</p>
        </div>
      </section>

      {/* Call to Action - Booking Section */}
      <section className="py-20 bg-gradient-to-br from-cyan-600 via-blue-600 to-blue-700 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <defs>
              <pattern id="waves" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                <path d="M0 10 Q5 5 10 10 T20 10" stroke="white" strokeWidth="0.5" fill="none"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#waves)"/>
          </svg>
        </div>
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            {language === 'de' ? 'Erlebe das Paradies des Windsurfens' : 'Experience the Paradise of Windsurfing'}
          </h2>
          <p className="text-xl md:text-2xl text-cyan-100 mb-8 leading-relaxed max-w-3xl mx-auto">
            {language === 'de' 
              ? 'Tauche ein in die unvergessliche Welt des Windsurfens in Soma Bay. Hier erwarten dich nicht nur perfekte Windbedingungen, sondern auch eine atemberaubende Landschaft und eine freundliche Community. Lass den Alltag hinter dir und genieße die Freiheit auf dem Wasser. Ob Anfänger oder Profi, bei uns findest du die perfekte Unterstützung und die besten Bedingungen für dein nächstes Abenteuer. Verpasse nicht die Gelegenheit, unvergessliche Momente zu erleben und deine Fähigkeiten zu verbessern.'
              : 'Dive into the unforgettable world of windsurfing in Soma Bay. Here you will find not only perfect wind conditions, but also a breathtaking landscape and a friendly community. Leave everyday life behind and enjoy the freedom on the water. Whether beginner or pro, we offer the perfect support and the best conditions for your next adventure. Don\'t miss the opportunity to experience unforgettable moments and improve your skills.'}
          </p>
          <a
            href="https://reservations.surfmotion.com/reservation"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center bg-white text-blue-600 px-10 py-4 rounded-full font-bold text-lg hover:bg-cyan-50 hover:shadow-2xl hover:scale-105 transition-all duration-300 transform"
            data-testid="cta-booking-link"
          >
            {language === 'de' ? 'Vorab-Registrierung' : 'Pre-Registration'}
            <ArrowRight className="ml-3 w-6 h-6" />
          </a>
        </div>
      </section>
    </div>
  );
};

export default Home;